// src/sites/CarForm.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import { BRANDS, MODELS_BY_BRAND } from "./vehicleData";
import { useI18n } from "../I18nProvider";
import { FORMULAR_URL } from "../formularZiel";
import FormMeldung from "./FormMeldung";

export default function CarForm({ wunsch, onFertig }) {
  const { t } = useI18n();
  const tr = (key, vars) => {
    let s = t(key);
    if (vars) {
      Object.keys(vars).forEach((k) => {
        s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
      });
    }
    return s;
  };

  // Die Discord-Adressen stehen NICHT mehr hier, sondern nur noch beim
  // Vermittler (siehe worker/). Hier wird bloss das Ziel benannt.
  const [serviceType, setServiceType] = useState("purchase"); // "purchase" | "wish_car"
  const [formData, setFormData] = useState({
    brand: "",
    model: "",
    brand_other: "",
    model_other: "",
    year: "",
    mileage: "",
    budget: "",
    equipment: "",
    color: "",
    fuel_type: "",
    getriebe: "",
    bauart: "",
    tueren: "",
    sitze: "",
    price_expectation: "",
    currency: "EUR", // Waehrung fuer Preis- bzw. Budgetangabe: EUR oder CHF
    name: "",
    email: "",
    phone: "",
    location: "",
    land: "",
    webseite: "", // Koeder, siehe Formularende
    message: "",
  });

  const [availableModels, setAvailableModels] = useState([]);
  const [filesInfo, setFilesInfo] = useState({ count: 0, names: [] });
  const imagesInputRef = useRef(null);
  const yearMax = useMemo(() => new Date().getFullYear(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hinweis, setHinweis] = useState(null);
  const [vorschau, setVorschau] = useState([]);

  // Klickt jemand oben auf eine der beiden Service-Karten, stellt sich das
  // Formular passend um. Der Zeitstempel im Wunsch sorgt dafuer, dass auch ein
  // erneuter Klick auf dieselbe Karte wieder ankommt.
  useEffect(() => {
    if (wunsch?.typ) setServiceType(wunsch.typ);
    if (wunsch?.auto) {
      const text = t("cars.anfrage_text").replace("{auto}", wunsch.auto);
      setFormData((prev) => ({
        ...prev,
        message: prev.message.includes(wunsch.auto) ? prev.message : text,
      }));
    }
  }, [wunsch, t]);

  // Waehrungsumschalter (EUR/CHF) - wird beim Preis UND beim Budget benutzt
  function waehrungsKnoepfe() {
    return (
      <div style={styles.currencyToggle} role="group" aria-label={t("carform.currency_label")}>
        {["EUR", "CHF"].map((w) => (
          <button
            type="button"
            key={w}
            aria-pressed={formData.currency === w}
            onClick={() => setFormData((prev) => ({ ...prev, currency: w }))}
            style={{
              ...styles.currencyBtn,
              ...(formData.currency === w ? styles.currencyBtnAktiv : {}),
            }}
          >
            {w === "EUR" ? "€ EUR" : "CHF"}
          </button>
        ))}
      </div>
    );
  }

  function handleChange(e) {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function onBrandChange(e) {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      brand: value,
      model: "",
      brand_other: value === "other" ? prev.brand_other : "",
      model_other: value === "other" ? prev.model_other : "",
    }));
    setAvailableModels(MODELS_BY_BRAND[value] || []);
  }

  const isOtherBrand = formData.brand === "other";

  function onFilesChange(e) {
    const files = Array.from(e.target.files || []);
    const limited = files.slice(0, 10);
    if (files.length > 10) {
      setHinweis({ typ: "fehler", text: t("carform.alert_max10") });
      if (imagesInputRef.current) {
        const dt = new DataTransfer();
        limited.forEach((f) => dt.items.add(f));
        imagesInputRef.current.files = dt.files;
      }
    }
    setFilesInfo({
      count: limited.length,
      names: limited.map((f) => f.name),
    });
    setVorschau((alt) => {
      alt.forEach((u) => URL.revokeObjectURL(u));
      return limited.map((f) => URL.createObjectURL(f));
    });
  }

  function validate() {
    const year = Number(formData.year);
    const mileage = Number(formData.mileage);
    const price = Number(formData.price_expectation);

    if (!formData.brand) return t("carform.err_brand");
    if (formData.brand === "other") {
      if (!formData.brand_other?.trim()) return t("carform.err_brand_other");
      if (!formData.model_other?.trim()) return t("carform.err_model_other");
    } else {
      if (!formData.model) return t("carform.err_model");
    }

    if (!year || year < 1990 || year > yearMax)
      return tr("carform.err_year_range", { min: 1990, max: yearMax });

    if (!mileage || mileage < 0) return t("carform.err_mileage");

    if (serviceType === "purchase") {
      if (!price || price < 0) return t("carform.err_price");
      if (!imagesInputRef.current || imagesInputRef.current.files.length === 0)
        return t("carform.err_images_required");
    }

    if (!formData.name || !formData.location || !formData.land)
      return t("carform.err_contacts");
    // Telefon ODER E-Mail genuegt. Beides zu verlangen kostet Anfragen,
    // ohne dass es die Erreichbarkeit verbessert - einer der beiden Wege
    // reicht, um sich zu melden.
    if (!formData.email && !formData.phone) return t("carform.err_kontaktweg");


    return null;
  }

  // Uebersetzt den Laender-Schluessel in den Klarnamen fuer die Discord-Meldung
  function landName(schluessel) {
    if (!schluessel) return "-";
    return t(`carform.land_${schluessel}`);
  }

  // Haengt die gewaehlte Waehrung an einen Geldbetrag ("20000" -> "20000 CHF").
  // Ohne das wuerde ein Schweizer Franken-Betrag bei uns als Euro ankommen.
  function mitWaehrung(betrag) {
    if (betrag === "" || betrag === null || betrag === undefined) return "-";
    const zeichen = formData.currency === "CHF" ? "CHF" : "EUR";
    return `${betrag} ${zeichen}`;
  }

  // Discord-Embed nur mit relevanten Feldern
  function buildDiscordEmbed({ brand, model }) {
    const title =
      serviceType === "purchase" ? t("carform.embed.title_purchase") : t("carform.embed.title_wish");
    const fields = [];

    // Basis
    fields.push(
      {
        name: t("carform.embed.service_name"),
        value:
          serviceType === "purchase"
            ? t("carform.service_purchase_full")
            : t("carform.service_wish_full"),
        inline: false,
      },
      { name: t("carform.field_brand"), value: brand || "-", inline: true },
      { name: t("carform.field_model"), value: model || "-", inline: true },
      { name: t("carform.field_year"), value: String(formData.year || "-"), inline: true },
      { name: t("carform.field_mileage"), value: String(formData.mileage || "-"), inline: true }
    );

    if (serviceType === "purchase") {
      fields.push({
        name: t("carform.field_price"),
        value: mitWaehrung(formData.price_expectation),
        inline: true,
      });
    }

    if (serviceType === "wish_car") {
      if (formData.budget)
        fields.push({ name: t("carform.field_budget"), value: mitWaehrung(formData.budget), inline: true });
      if (formData.color)
        fields.push({ name: t("carform.field_color"), value: formData.color, inline: true });
      if (formData.fuel_type)
        fields.push({ name: t("carform.field_fuel"), value: mapFuel(formData.fuel_type), inline: true });
      if (formData.getriebe)
        fields.push({ name: t("carform.field_getriebe"), value: mapWahl("getriebe", formData.getriebe), inline: true });
      if (formData.bauart)
        fields.push({ name: t("carform.field_bauart"), value: mapWahl("bauart", formData.bauart), inline: true });
      if (formData.tueren)
        fields.push({ name: t("carform.field_tueren"), value: mapWahl("tueren", formData.tueren), inline: true });
      if (formData.sitze)
        fields.push({ name: t("carform.field_sitze"), value: mapWahl("sitze", formData.sitze), inline: true });
      if (formData.equipment)
        fields.push({ name: t("carform.field_equipment"), value: formData.equipment, inline: false });
    }

    // Kontakt
    fields.push(
      { name: t("carform.field_name"), value: formData.name || "-", inline: true },
      { name: t("carform.field_phone"), value: formData.phone || "-", inline: true },
      { name: t("carform.field_email"), value: formData.email || "-", inline: true },
      { name: t("carform.field_location"), value: formData.location || "-", inline: true },
      { name: t("carform.field_land"), value: landName(formData.land), inline: true }
    );

    if (formData.message) {
      fields.push({ name: t("carform.field_message"), value: formData.message, inline: false });
    }

    return {
      title,
      color: 0x1a3a5f,
      fields,
      timestamp: new Date().toISOString(),
    };
  }

  // Wandelt die gespeicherten Kuerzel in lesbare Woerter fuer die Meldung.
  // "egal" ist bewusst ein eigener Wert und kein leeres Feld: "ist mir egal"
  // ist eine Information, "nicht ausgefuellt" ist keine.
  function mapWahl(bereich, wert) {
    if (!wert) return "";
    if (wert === "egal") return t("carform.egal");
    return t(`carform.${bereich}_${wert}`);
  }

  function mapFuel(f) {
    switch (f) {
      case "petrol":
        return t("carform.fuel_petrol");
      case "diesel":
        return t("carform.fuel_diesel");
      case "hybrid":
        return t("carform.fuel_hybrid");
      case "electric":
        return t("carform.fuel_electric");
      case "any":
        return t("carform.fuel_any");
      default:
        return "-";
    }
  }

  async function postToDiscord({ embed, filesForDiscord }) {
    // Stichwort statt Adresse - der Vermittler kennt den richtigen Kanal
    const ziel = serviceType === "purchase" ? "ankauf" : "wunschauto";

    const fd = new FormData();
    fd.append("ziel", ziel);
    fd.append("webseite", formData.webseite);
    const payload = {
      username: "AUTO M.A.H. Formular",
      content:
        serviceType === "purchase"
          ? t("carform.discord_content_purchase")
          : t("carform.discord_content_wish"),
      embeds: [embed],
    };
    fd.append("payload_json", JSON.stringify(payload));

    // Nur bei purchase: Bilder mitsenden (max. 10)
    if (serviceType === "purchase" && filesForDiscord?.length) {
      filesForDiscord.forEach((file, idx) => {
        fd.append(`files[${idx}]`, file, file.name || `image_${idx + 1}.jpg`);
      });
    }

    const res = await fetch(FORMULAR_URL, {
      method: "POST",
      body: fd,
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || t("carform.err_webhook"));
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    const error = validate();
    if (error) {
      setHinweis({ typ: "fehler", text: error });
      return;
    }

    setHinweis(null);
    setIsSubmitting(true);
    try {
      // Normalisierte Marke/Modell
      const brandNormalized =
        formData.brand === "other" ? formData.brand_other || t("carform.brand_other_fallback") : formData.brand;
      const modelNormalized =
        formData.brand === "other" ? formData.model_other : formData.model;

      const embed = buildDiscordEmbed({
        brand: brandNormalized,
        model: modelNormalized,
      });

      // purchase: Dateien an Discord (max 10)
      let filesForDiscord = [];
      if (serviceType === "purchase" && imagesInputRef.current?.files?.length) {
        const selected = Array.from(imagesInputRef.current.files);
        filesForDiscord = selected.slice(0, 10);
        if (selected.length > 10) {
          console.warn(t("carform.warn_only_first10"));
        }
      }

      await postToDiscord({ embed, filesForDiscord });

      setHinweis({ typ: "ok", text: t("carform.success_alert") });

      // Reset
      if (e.target && typeof e.target.reset === "function") e.target.reset();
      if (imagesInputRef.current) imagesInputRef.current.value = "";
      setFilesInfo({ count: 0, names: [] });
      setVorschau((alt) => {
        alt.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
      setServiceType("purchase");
      setFormData({
        brand: "",
        model: "",
        brand_other: "",
        model_other: "",
        year: "",
        mileage: "",
        budget: "",
        equipment: "",
        color: "",
        fuel_type: "",
        price_expectation: "",
        currency: "EUR",
        name: "",
        email: "",
        phone: "",
        location: "",
        land: "",
        getriebe: "",
        bauart: "",
        tueren: "",
        sitze: "",
        message: "",
          });
      setAvailableModels([]);
      window.setTimeout(() => onFertig?.(), 1600);
    } catch (err) {
      console.error(err);
      setHinweis({ typ: "fehler", text: t("carform.err_generic") });
    } finally {
      setIsSubmitting(false);
    }
  }

  // Inline-Minimalstyles (Hover-Farbe via state)
  const styles = {
    form: { margin: "0 auto", padding: 16, border: "1px solid #ddd", borderRadius: 8 },
    section: { marginBottom: 24 },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 },
    full: { gridColumn: "1 / -1" },
    label: { display: "block", fontWeight: 600, marginBottom: 6 },
    input: { width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6 },
    textarea: { width: "100%", padding: 10, border: "1px solid #ccc", borderRadius: 6, minHeight: 80 },
    h3: { marginTop: 0 },
    h4: { marginBottom: 8 },
    small: { display: "block", color: "#555", marginTop: 8 },
    ul: { marginTop: 6 },
    button: {
      padding: "12px 18px",
      border: "none",
      borderRadius: 8,
      background: isHovered ? "#b8941f" : "#d4af37",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
      opacity: isSubmitting ? 0.7 : 1,
    },
    // Preis-Zeile: Betragsfeld + Waehrungsknoepfe nebeneinander.
    // WICHTIG: styles.css setzt global "input, select { width: 100% }".
    // Darum braucht das Betragsfeld hier ausdruecklich width:auto + flex,
    // sonst draengt es das Nachbarelement aus der Zeile.
    moneyRow: { display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" },
    moneyInput: {
      flex: "1 1 180px",
      width: "auto",
      minWidth: 0,
      padding: 10,
      border: "1px solid #ccc",
      borderRadius: 6,
      fontFamily: "inherit",
      fontSize: "1rem",
    },
    currencyToggle: { display: "flex", gap: 6, flex: "0 0 auto" },
    currencyBtn: {
      width: "auto",
      padding: "10px 18px",
      border: "1px solid #ccc",
      borderRadius: 6,
      background: "#fff",
      color: "#555",
      fontWeight: 600,
      cursor: "pointer",
      fontSize: "1rem",
      fontFamily: "inherit",
      minWidth: 76,
      transition: "all 0.2s ease",
    },
    currencyBtnAktiv: {
      background: "#d4af37",
      borderColor: "#d4af37",
      color: "#fff",
    },
    fileWrap: { border: "1px dashed #aaa", borderRadius: 8, padding: 12, background: "#fafafa" },
    checkboxLabel: { display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" },
    radioWrap: { display: "flex", gap: 24 },
  };

  return (
    <div className="purchase-form" style={styles.form}>
      <h3 style={styles.h3}>{t("carform.title")}</h3>

      <form id="vehicleForm" onSubmit={onSubmit} noValidate>
        {/* Service-Auswahl */}
        {/* Service-Auswahl NUR zeigen, wenn das Formular ohne festen Auftrag
            geoeffnet wurde. Wer auf "Ankauf anfragen" oder "Wunschauto finden"
            geklickt hat, hat sich bereits entschieden. Ihm hier noch einmal
            beide Moeglichkeiten hinzustellen, saet nur Zweifel und laedt zum
            versehentlichen Umschalten ein. Welcher Service laeuft, steht in
            der Kopfzeile des Fensters. */}
        {!wunsch?.typ && (
        <div className="form-section" style={styles.section}>
          <h4 style={styles.h4}>{t("carform.service_title")}</h4>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "10px" }}>
            {/* Echte Knoepfe statt <div>: dadurch mit der Tastatur bedienbar
                und mit Hover-Farbe. Aussehen steckt in styles.css unter
                .service-wahl. */}
            <button
              type="button"
              className={`service-wahl ${serviceType === "purchase" ? "aktiv" : ""}`}
              aria-pressed={serviceType === "purchase"}
              onClick={() => setServiceType("purchase")}
            >
              <i className="fas fa-car-side" aria-hidden="true"></i>
              {t("carform.service_purchase")}
            </button>

            <button
              type="button"
              className={`service-wahl ${serviceType === "wish_car" ? "aktiv" : ""}`}
              aria-pressed={serviceType === "wish_car"}
              onClick={() => setServiceType("wish_car")}
            >
              <i className="fas fa-magnifying-glass-location" aria-hidden="true"></i>
              {t("carform.service_wish")}
            </button>
          </div>
        </div>
        )}

        {/* Fahrzeug-Daten */}
        <div className="form-section" style={styles.section}>
          <h4 style={styles.h4}>{t("carform.vehicle_title")}</h4>
          <div className="form-grid" style={styles.grid}>
            <div className="form-group">
              <label htmlFor="brand" style={styles.label}>
                {t("carform.brand_label")}
              </label>
              <select
                id="brand"
                name="brand"
                required
                value={formData.brand}
                onChange={onBrandChange}
                style={styles.input}
              >
                <option value="">{t("carform.brand_placeholder")}</option>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>

            {isOtherBrand ? (
              <>
                <div className="form-group">
                  <label htmlFor="brand_other" style={styles.label}>
                    {t("carform.brand_other_label")}
                  </label>
                  <input
                    type="text"
                    id="brand_other"
                    name="brand_other"
                    placeholder={t("carform.brand_other_ph")}
                    required
                    value={formData.brand_other}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="model_other" style={styles.label}>
                    {t("carform.model_other_label")}
                  </label>
                  <input
                    type="text"
                    id="model_other"
                    name="model_other"
                    placeholder={t("carform.model_other_ph")}
                    required
                    value={formData.model_other}
                    onChange={handleChange}
                    style={styles.input}
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label htmlFor="model" style={styles.label}>
                  {t("carform.model_label")}
                </label>
                <select
                  id="model"
                  name="model"
                  required
                  value={formData.model}
                  onChange={handleChange}
                  style={styles.input}
                  disabled={!formData.brand}
                >
                  <option value="">
                    {formData.brand ? t("carform.model_placeholder") : t("carform.model_first_choose")}
                  </option>
                  {availableModels.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="year" style={styles.label}>
                {t("carform.year_label")}
              </label>
              <input
                type="number"
                id="year"
                name="year"
                min={1990}
                max={yearMax}
                placeholder={tr("carform.year_ph", { example: yearMax - 5 })}
                required
                value={formData.year}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mileage" style={styles.label}>
                {t("carform.mileage_label")}
              </label>
              <input
                type="number"
                id="mileage"
                name="mileage"
                placeholder={t("carform.mileage_ph")}
                required
                value={formData.mileage}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            {/* Getriebe, Karosserieform, Tueren und Sitze gehoeren zu den
                Fahrzeugdaten, nicht zur Wunschausstattung. Nur beim
                Wunschauto sichtbar: beim Ankauf sieht Mohamed das alles
                ohnehin auf den Fotos. Freiwillig, darum ohne Stern. */}
            {serviceType === "wish_car" && (
              <>

                <div className="form-group">
                  <label htmlFor="fuel_type" style={styles.label}>
                    {t("carform.fuel_label")}
                  </label>
                  <select
                    id="fuel_type"
                    name="fuel_type"
                    value={formData.fuel_type}
                    onChange={handleChange}
                    style={styles.input}
                  >
                    <option value="">{t("carform.fuel_placeholder")}</option>
                    <option value="petrol">{t("carform.fuel_petrol")}</option>
                    <option value="diesel">{t("carform.fuel_diesel")}</option>
                    <option value="hybrid">{t("carform.fuel_hybrid")}</option>
                    <option value="electric">{t("carform.fuel_electric")}</option>
                    <option value="any">{t("carform.fuel_any")}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="getriebe" style={styles.label}>
                    {t("carform.getriebe_label")}
                  </label>
                  <select
                    id="getriebe"
                    name="getriebe"
                    value={formData.getriebe}
                    onChange={handleChange}
                    style={styles.input}
                  >
                    <option value="">{t("carform.fuel_placeholder")}</option>
                    <option value="auto">{t("carform.getriebe_auto")}</option>
                    <option value="hand">{t("carform.getriebe_hand")}</option>
                    <option value="egal">{t("carform.egal")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="bauart" style={styles.label}>
                    {t("carform.bauart_label")}
                  </label>
                  <select
                    id="bauart"
                    name="bauart"
                    value={formData.bauart}
                    onChange={handleChange}
                    style={styles.input}
                  >
                    <option value="">{t("carform.fuel_placeholder")}</option>
                    <option value="limousine">{t("carform.bauart_limousine")}</option>
                    <option value="kombi">{t("carform.bauart_kombi")}</option>
                    <option value="suv">{t("carform.bauart_suv")}</option>
                    <option value="coupe">{t("carform.bauart_coupe")}</option>
                    <option value="cabrio">{t("carform.bauart_cabrio")}</option>
                    <option value="klein">{t("carform.bauart_klein")}</option>
                    <option value="van">{t("carform.bauart_van")}</option>
                    <option value="pickup">{t("carform.bauart_pickup")}</option>
                    <option value="egal">{t("carform.egal")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="tueren" style={styles.label}>
                    {t("carform.tueren_label")}
                  </label>
                  <select
                    id="tueren"
                    name="tueren"
                    value={formData.tueren}
                    onChange={handleChange}
                    style={styles.input}
                  >
                    <option value="">{t("carform.fuel_placeholder")}</option>
                    <option value="23">{t("carform.tueren_23")}</option>
                    <option value="45">{t("carform.tueren_45")}</option>
                    <option value="egal">{t("carform.egal")}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="sitze" style={styles.label}>
                    {t("carform.sitze_label")}
                  </label>
                  <select
                    id="sitze"
                    name="sitze"
                    value={formData.sitze}
                    onChange={handleChange}
                    style={styles.input}
                  >
                    <option value="">{t("carform.fuel_placeholder")}</option>
                    <option value="2">{t("carform.sitze_2")}</option>
                    <option value="45">{t("carform.sitze_45")}</option>
                    <option value="67">{t("carform.sitze_67")}</option>
                    <option value="mehr">{t("carform.sitze_mehr")}</option>
                    <option value="egal">{t("carform.egal")}</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Preisvorstellung (nur purchase) */}
        {serviceType === "purchase" && (
          <div className="form-section" style={styles.section}>
            <h4 style={styles.h4}>{t("carform.price_title")}</h4>
            <div className="form-grid" style={styles.grid}>
              <div className="form-group" style={styles.full}>
                <label htmlFor="price_expectation" style={styles.label}>
                  {t("carform.price_label")}
                </label>
                <div style={styles.moneyRow}>
                  <input
                    type="number"
                    id="price_expectation"
                    name="price_expectation"
                    placeholder={t("carform.price_ph")}
                    required
                    value={formData.price_expectation}
                    onChange={handleChange}
                    style={styles.moneyInput}
                  />
                  {waehrungsKnoepfe()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Budgetvorstellung (nur wish_car) */}
        {serviceType === "wish_car" && (
          <div className="form-section" id="wishCarFields" style={styles.section}>
            <h4 style={styles.h4}>{t("carform.budget_title")}</h4>
            <div className="form-grid" style={styles.grid}>
              <div className="form-group">
                <label htmlFor="budget" style={styles.label}>
                  {t("carform.budget_label")}
                </label>
                <div style={styles.moneyRow}>
                  <input
                    type="number"
                    id="budget"
                    name="budget"
                    placeholder={t("carform.budget_ph")}
                    value={formData.budget}
                    onChange={handleChange}
                    style={styles.moneyInput}
                  />
                  {waehrungsKnoepfe()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wunschauto-Felder (nur wish_car).
            ALLE Felder hier sind freiwillig, darum zugeklappt: wer nichts
            Bestimmtes will, scrollt nicht daran vorbei. Pflichtfelder
            duerfen NIE zugeklappt sein - man kann sonst einen Fehler
            bekommen, ohne das Feld zu sehen. */}
        {serviceType === "wish_car" && (
          <details className="form-section optional-block" id="wishCarFields2" style={styles.section}>
            <summary>
              {t("carform.features_title")}
              <span className="optional-marke">{t("carform.optional_marke")}</span>
              <i className="fas fa-chevron-down" aria-hidden="true"></i>
            </summary>
            <div className="form-grid" style={styles.grid}>
              <div className="form-group" style={styles.full}>
                <label htmlFor="equipment" style={styles.label}>
                  {t("carform.equipment_label")}
                </label>
                <textarea
                  id="equipment"
                  name="equipment"
                  placeholder={t("carform.equipment_ph")}
                  value={formData.equipment}
                  onChange={handleChange}
                  style={styles.textarea}
                />
              </div>

              <div className="form-group">
                <label htmlFor="color" style={styles.label}>
                  {t("carform.color_label")}
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  placeholder={t("carform.color_ph")}
                  value={formData.color}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>
          </details>
        )}

        {/* Bild-Upload (nur purchase) */}
        {serviceType === "purchase" && (
          <div className="form-section" style={styles.section}>
            <h4 style={styles.h4}>{t("carform.photos_title")}</h4>
            <div className="form-group" style={styles.full}>
              <label style={styles.label}>{t("carform.photos_label")}</label>

              <div className="file-upload-container" style={styles.fileWrap}>
                <input
                  ref={imagesInputRef}
                  type="file"
                  id="images"
                  name="images"
                  multiple
                  accept="image/*"
                  required
                  className="file-input"
                  onChange={onFilesChange}
                  style={{ display: "block" }}
                />
                <div style={{ marginTop: 8 }}>
                  <strong>
                    {filesInfo.count === 0
                      ? t("carform.files_none")
                      : tr("carform.files_count", {
                          count: filesInfo.count,
                          names: filesInfo.names.join(", "),
                        })}
                  </strong>
                </div>
                {vorschau.length > 0 && (
                  <div className="foto-vorschau">
                    {vorschau.map((src, i) => (
                      <img key={src} src={src} alt="" />
                    ))}
                  </div>
                )}
              </div>

              <small style={styles.small}>{t("carform.photos_hint")}</small>

              <div className="image-requirements" style={{ marginTop: 10 }}>
                <h5 style={{ margin: "6px 0" }}>{t("carform.req_title")}</h5>
                <ul style={styles.ul}>
                  <li>{t("carform.req_front")}</li>
                  <li>{t("carform.req_rear")}</li>
                  <li>{t("carform.req_sides")}</li>
                  <li>{t("carform.req_interior")}</li>
                  <li>{t("carform.req_odo")}</li>
                  <li>{t("carform.req_damage")}</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Kontaktdaten */}
        <div className="form-section" style={styles.section}>
          <h4 style={styles.h4}>{t("carform.contacts_title")}</h4>
          <div className="form-grid" style={styles.grid}>
            <div className="form-group">
              <label htmlFor="name" style={styles.label}>
                {t("carform.name_label")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder={t("carform.name_ph")}
                required
                value={formData.name}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" style={styles.label}>
                {t("carform.email_label")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={t("carform.email_ph")}
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" style={styles.label}>
                {t("carform.phone_label")}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder={t("carform.phone_ph")}
                value={formData.phone}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            <div className="form-group">
              <label htmlFor="location" style={styles.label}>
                {t("carform.location_label")}
              </label>
              <input
                type="text"
                id="location"
                name="location"
                placeholder={t("carform.location_ph")}
                required
                value={formData.location}
                onChange={handleChange}
                style={styles.input}
              />
            </div>

            {/* Land: DE und FR haben beide 5-stellige PLZ, ohne Land ist der
                Standort nicht eindeutig (und beim Zoll macht es den Unterschied) */}
            <div className="form-group">
              <label htmlFor="land" style={styles.label}>
                {t("carform.land_label")}
              </label>
              <select
                id="land"
                name="land"
                required
                value={formData.land}
                onChange={handleChange}
                style={styles.input}
              >
                <option value="">{t("carform.land_placeholder")}</option>
                <option value="de">{t("carform.land_de")}</option>
                <option value="ch">{t("carform.land_ch")}</option>
                <option value="fr">{t("carform.land_fr")}</option>
                <option value="other">{t("carform.land_other")}</option>
              </select>
            </div>
          </div>

          <p className="kontaktweg-hinweis">
            <i className="fas fa-circle-info" aria-hidden="true"></i>
            {t("carform.kontaktweg_hinweis")}
          </p>

          <div className="form-group" style={styles.full}>
            <label htmlFor="message" style={styles.label}>
              {t("carform.message_label")}
            </label>
            <textarea
              id="message"
              name="message"
              rows={3}
              placeholder={t("carform.message_ph")}
              value={formData.message}
              onChange={handleChange}
              style={styles.textarea}
            />
          </div>

          {/* Koederfeld gegen Automaten. Fuer Menschen unsichtbar UND nicht
              erreichbar: auf 1x1 Punkt geschrumpft und weggeschnitten, aus der Tabulator-Reihenfolge
              genommen, fuer Vorleseprogramme ausgeblendet. Ein Mensch kann es
              gar nicht ausfuellen. Ein Automat fuellt blind jedes Feld und
              verraet sich damit. Der Vermittler verwirft solche Anfragen still
              und meldet trotzdem Erfolg, damit der Automat nichts dazulernt. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              margin: -1,
              padding: 0,
              border: 0,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              clipPath: "inset(50%)",
              whiteSpace: "nowrap",
            }}
          >
            <label htmlFor="hp-fahrzeug">Bitte dieses Feld leer lassen</label>
            <input
              type="text"
              id="hp-fahrzeug"
              name="webseite"
              tabIndex={-1}
              autoComplete="off"
              value={formData.webseite}
              onChange={handleChange}
            />
          </div>

          <div className="form-group" style={styles.full}>
            {/* Kein Pflichthaken: eine Datenschutzerklaerung ist eine Information,
                keine Einwilligung. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b/f DSGVO. */}
            <p style={{ fontSize: 13, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>
              {t("carform.privacy_prefix")}{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#d4af37", textDecoration: "underline" }}
              >
                {t("carform.privacy_link")}
              </a>{" "}
              {t("carform.privacy_suffix")}
            </p>
          </div>
        </div>

        <FormMeldung hinweis={hinweis} />

        {/* Submit */}
        {serviceType === "purchase" && (
          <button
            type="submit"
            className="btn submit-btn"
            style={styles.button}
            disabled={isSubmitting}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isSubmitting ? t("carform.btn_sending") : t("carform.btn_submit_purchase")}
          </button>
        )}

        {serviceType === "wish_car" && (
          <button
            type="submit"
            className="btn submit-btn"
            style={styles.button}
            disabled={isSubmitting}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isSubmitting ? t("carform.btn_sending") : t("carform.btn_submit_wish")}
          </button>
        )}
      </form>
    </div>
  );
}
